// The Usuarios screen for a business on the cierres access model — BR-37. Where an administrator
// gives people access: per person a list of assignments (a role plus where it applies), the roles
// themselves, and each person's change history.
//
// `BusinessUsers.jsx` decides when this shows (`accesoConfigurado`): only for a business whose
// people hold assignments, which `npm run access:seed` writes for everyone at once. Every other
// tenant keeps that file's screen unchanged. Creating a user, editing their personal data and
// deleting them are still that file's — the legacy user routes (BR-33) — handed in as callbacks;
// what a person may do in Cierres is decided here and nowhere else.
//
// Who appears in the list is the backend's answer (`/access/users`): the caller, and everyone the
// caller may manage — nobody else. A Supervisor of Davivienda does not see an Auditor of
// Bancolombia here at all, and the scope picker never offers a client outside their own reach.
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, History, KeyRound, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Badge, Button, Spinner } from "@mantis-sas/ui";

import { getAccessRoles, getAccessUsers } from "../../../ApiCalls/mantisAccess";
import { listCierreClients } from "../../../ApiCalls/mantisBusiness";
import { conNombresDePermiso, MODOS_DE_ACCESO, nombreDeRol } from "../../../utils/accesoDiccionario";
import { describirAlcance, gruposDeUsuarios, nombreDeUsuario, puedeAdministrarUsuarios } from "../../../utils/cierreAcceso";
import { AccesoAsignaciones } from "./AccesoAsignaciones";
import { AccesoHistorial } from "./AccesoHistorial";
import { AccesoRoles } from "./AccesoRoles";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

const AVISO = "rounded-[8px] border px-3 py-2 text-[12.5px]";
const AVISO_INFO = `${AVISO} border-brinks-border bg-brinks-surface text-brinks-foreground`;
const AVISO_ERROR = `${AVISO} border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger`;
const AVISO_OK = `${AVISO} border-brinks-success-border bg-brinks-success-soft text-brinks-success`;

const PESTANAS = [
  { clave: "usuarios", etiqueta: "Usuarios" },
  { clave: "roles", etiqueta: "Roles" },
];

function ListaDeAsignaciones({ asignaciones, sinResolver, roles, nombreDeCliente }) {
  if (!asignaciones || asignaciones.length === 0) {
    return <Badge variant="warning">Sin acceso</Badge>;
  }
  return (
    <ul className="space-y-0.5">
      {asignaciones.map((a, i) => (
        <li key={a.id || i} className="text-[12.5px] text-brinks-foreground">
          <span className="font-semibold">{a.roleName || nombreDeRol(a.role, roles)}</span>
          <span className="text-brinks-muted-foreground"> — {describirAlcance(a.scope, nombreDeCliente)}</span>
          {(sinResolver || []).includes(a.role) ? (
            <span className="ml-1 text-[11.5px] text-brinks-danger">(este rol ya no existe y no otorga nada)</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// B-174 — under `sm` a row is a card (the same markup, laid out as blocks): name, access, then the
// actions in a row of their own, each a touch target.
function FilaDeUsuario({ usuario, roles, nombreDeCliente, onAcceso, onHistorial, onEditarDatos, onEliminar }) {
  const iniciales = `${usuario.firstName?.charAt(0) || ""}${usuario.lastName?.charAt(0) || ""}` || "?";
  return (
    <tr className="border-t border-brinks-border align-top max-sm:block max-sm:rounded-[12px] max-sm:border max-sm:bg-card max-sm:p-3 max-sm:shadow-brinks-card">
      <td className="px-3 py-2.5 max-sm:block max-sm:p-0">
        <div className="flex items-center gap-2.5">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brinks-surface text-[12px] font-semibold text-brinks-foreground">
            {iniciales.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[13px] font-medium text-brinks-foreground">
              {nombreDeUsuario(usuario)}
              {usuario.self ? <Badge variant="secondary">Tú</Badge> : null}
            </p>
            {usuario.email ? <p className="break-all text-[12px] text-brinks-muted-foreground sm:truncate">{usuario.email}</p> : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 max-sm:mt-2.5 max-sm:block max-sm:border-t max-sm:border-brinks-border max-sm:px-0 max-sm:pb-0 max-sm:pt-2.5">
        <ListaDeAsignaciones
          asignaciones={usuario.assignments}
          sinResolver={usuario.unresolvedRoles}
          roles={roles}
          nombreDeCliente={nombreDeCliente}
        />
      </td>
      <td className="px-3 py-2.5 max-sm:mt-3 max-sm:block max-sm:p-0">
        <div className="flex flex-wrap gap-1 max-sm:gap-2 sm:justify-end">
          {usuario.editable ? (
            <Button type="button" size="sm" className="h-10 gap-1 sm:h-7" onClick={onAcceso}>
              <KeyRound className="h-3.5 w-3.5" />
              Acceso
            </Button>
          ) : (
            <span className="self-center text-[11.5px] text-brinks-muted-foreground">No puedes cambiar tu propio acceso</span>
          )}
          <Button type="button" size="sm" variant="outline" className="h-10 gap-1 sm:h-7" onClick={onHistorial}>
            <History className="h-3.5 w-3.5" />
            Historial
          </Button>
          {onEditarDatos ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-10 w-10 p-0 sm:h-7 sm:w-7"
              aria-label={`Editar datos de ${nombreDeUsuario(usuario)}`}
              title="Editar datos"
              onClick={onEditarDatos}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onEliminar && !usuario.self ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-10 w-10 p-0 text-brinks-danger sm:h-7 sm:w-7"
              aria-label={`Eliminar a ${nombreDeUsuario(usuario)}`}
              title="Eliminar usuario"
              onClick={onEliminar}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export function AccesoUsuarios({ token, mantisBusinessId, me, recarga = 0, onCrearUsuario, onEditarDatos, onEliminar }) {
  const [pestana, setPestana] = useState("usuarios");
  const [usuarios, setUsuarios] = useState(null);
  const [errorUsuarios, setErrorUsuarios] = useState("");
  const [roles, setRoles] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [plegados, setPlegados] = useState([]); // the role groups folded away; every group starts open
  const [editando, setEditando] = useState(null);
  const [historialDe, setHistorialDe] = useState(null);
  const [aviso, setAviso] = useState(null);

  const administra = puedeAdministrarUsuarios(me);

  const cargarUsuarios = useCallback(async () => {
    if (!administra) {
      setUsuarios([]);
      return [];
    }
    const response = await getAccessUsers(token).catch(() => null);
    if (!response?.success) {
      setErrorUsuarios(conNombresDePermiso(response?.message) || "No se pudo cargar la lista de usuarios.");
      setUsuarios([]);
      return [];
    }
    setErrorUsuarios("");
    setUsuarios(response.data || []);
    return response.data || [];
  }, [token, administra]);

  const cargarRoles = useCallback(async () => {
    const response = await getAccessRoles(token).catch(() => null);
    if (response?.success) setRoles(response.data.roles || []);
  }, [token]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios, recarga]);

  useEffect(() => {
    cargarRoles();
  }, [cargarRoles]);

  // The pickers' clients. Under `enforced` the backend answers only the caller's own; the scope
  // picker narrows further to what they may grant. `me.clients` (names only) stands in if the list
  // fails, so assignments still read with names.
  useEffect(() => {
    let activo = true;
    (async () => {
      const response = await listCierreClients(token, mantisBusinessId).catch(() => null);
      if (!activo) return;
      if (response?.success) setClientes(response.data.clients || []);
      else setClientes((me?.clients || []).map((c) => ({ code: c.code, name: c.name })));
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, me]);

  const nombreDeCliente = useCallback((code) => clientes.find((c) => c.code === code)?.name || code, [clientes]);

  const visibles = useMemo(() => {
    const texto = filtro.trim().toLowerCase();
    if (!texto) return usuarios || [];
    return (usuarios || []).filter((u) => `${nombreDeUsuario(u)} ${u.email || ""}`.toLowerCase().includes(texto));
  }, [usuarios, filtro]);

  const guardado = async (resultado) => {
    const nombre = editando ? nombreDeUsuario(editando) : "";
    setEditando(null);
    setAviso({
      tono: resultado.logged === false ? "error" : "ok",
      texto: !resultado.changed
        ? `El acceso de ${nombre} no cambió.`
        : resultado.logged === false
        ? `El acceso de ${nombre} se guardó, pero no quedó en el historial. Avisa a Mantis.`
        : `Acceso de ${nombre} guardado.`,
    });
    await cargarUsuarios();
  };

  const recargarEditando = async () => {
    const lista = await cargarUsuarios();
    setEditando((actual) => (actual ? lista.find((u) => u.id === actual.id) || null : null));
  };

  const modo = MODOS_DE_ACCESO[me?.mode] || MODOS_DE_ACCESO.off;

  return (
    <div className="brinks-scope font-redhat min-w-0 flex-1 bg-background p-3 sm:p-4">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-lora text-[21px] font-semibold text-brinks-foreground">Usuarios y acceso</h1>
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">
            Quién puede ver y hacer qué, en qué clientes y oficinas
          </p>
        </div>
        {onCrearUsuario ? (
          <Button type="button" size="sm" className="h-8 gap-1.5" onClick={onCrearUsuario}>
            <Plus className="h-4 w-4" />
            Crear usuario
          </Button>
        ) : null}
      </header>

      <p className={`${AVISO_INFO} mb-3`}>
        <span className="font-semibold">Modo de acceso: {modo.nombre}. </span>
        {modo.aviso}
      </p>

      <div role="tablist" aria-label="Secciones de acceso" className="mb-3 inline-flex h-8 items-center rounded-[8px] border bg-card p-0.5">
        {PESTANAS.map((p) => (
          <button
            key={p.clave}
            type="button"
            role="tab"
            aria-selected={pestana === p.clave}
            onClick={() => setPestana(p.clave)}
            className={`inline-flex h-7 items-center rounded-[6px] px-3 text-[12.5px] font-medium transition ${
              pestana === p.clave ? "bg-primary text-primary-foreground" : "text-brinks-muted-foreground hover:bg-brinks-surface-hover"
            }`}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>

      {pestana === "roles" ? (
        <AccesoRoles token={token} me={me} roles={roles} onCambio={cargarRoles} />
      ) : (
        <div className="space-y-3">
          <section className="rounded-[10px] border border-brinks-border bg-card p-3">
            <h2 className="mb-1 text-[13px] font-semibold text-brinks-foreground">Tu acceso</h2>
            <ListaDeAsignaciones asignaciones={me?.assignments} sinResolver={me?.unresolvedRoles} roles={roles} nombreDeCliente={nombreDeCliente} />
          </section>

          {aviso ? (
            <p role="status" className={aviso.tono === "ok" ? AVISO_OK : AVISO_ERROR}>
              {aviso.texto}
            </p>
          ) : null}

          {!administra ? (
            <p className={AVISO_INFO}>
              No tienes permiso para administrar el acceso de otras personas. Pídeselo a un administrador de tu negocio.
            </p>
          ) : usuarios === null ? (
            <div className="grid place-items-center py-10">
              <Spinner />
            </div>
          ) : (
            <>
              {errorUsuarios ? (
                <p role="alert" className={AVISO_ERROR}>
                  {errorUsuarios}
                </p>
              ) : null}
              <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
                <input
                  type="search"
                  {...guia("Buscar", "Encuentra a una persona por su nombre o su correo.")} aria-label="Buscar usuario"
                  placeholder="Buscar por nombre o correo…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="h-10 w-full rounded-[8px] border border-brinks-border bg-card pl-8 pr-2.5 text-[12.5px] text-brinks-foreground sm:h-8"
                />
              </div>
              <div {...guia("Personas y acceso", "El rol de cada persona y sobre qué clientes, regiones u oficinas lo tiene. \"Acceso\" lo cambia e \"Historial\" muestra quién lo cambió.")} className="overflow-x-auto rounded-[10px] border border-brinks-border bg-card max-sm:overflow-visible max-sm:border-0 max-sm:bg-transparent">
                <table className="w-full text-left max-sm:block sm:min-w-[640px]">
                  <thead className="bg-brinks-surface max-sm:hidden">
                    <tr>
                      <th className="px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Usuario</th>
                      <th className="px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Acceso</th>
                      <th className="px-3 py-2 text-right text-[11.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="max-sm:block max-sm:space-y-2">
                    {visibles.length === 0 ? (
                      <tr className="max-sm:block">
                        <td colSpan={3} className="px-3 py-6 text-center max-sm:block text-[12.5px] text-brinks-muted-foreground">
                          {filtro ? "Nadie coincide con la búsqueda." : "No hay usuarios que puedas administrar."}
                        </td>
                      </tr>
                    ) : (
                      gruposDeUsuarios(visibles, roles).map((grupo) => {
                        // "Tú" is one row and never folds; a search opens every group it matched.
                        const plegable = grupo.clave !== "tu";
                        const abierto = !plegable || Boolean(filtro.trim()) || !plegados.includes(grupo.clave);
                        const alternar = () =>
                          setPlegados((actual) =>
                            actual.includes(grupo.clave) ? actual.filter((c) => c !== grupo.clave) : [...actual, grupo.clave]
                          );
                        return (
                          <Fragment key={grupo.clave}>
                            <tr className="border-t border-brinks-border bg-brinks-surface max-sm:block max-sm:rounded-[10px] max-sm:border-0">
                              <th colSpan={3} scope="colgroup" className="p-0 text-left max-sm:block">
                                {plegable ? (
                                  <button
                                    type="button"
                                    aria-expanded={abierto}
                                    onClick={alternar}
                                    className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[11px] max-sm:min-h-10 max-sm:rounded-[10px] font-semibold uppercase tracking-wide text-brinks-muted-foreground hover:bg-brinks-surface-hover"
                                  >
                                    <ChevronDown className={`h-3.5 w-3.5 transition ${abierto ? "" : "-rotate-90"}`} />
                                    {grupo.titulo}
                                    <span className="font-normal">· {grupo.usuarios.length}</span>
                                  </button>
                                ) : (
                                  <span className="block px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">
                                    {grupo.titulo}
                                  </span>
                                )}
                              </th>
                            </tr>
                            {(abierto ? grupo.usuarios : []).map((usuario) => (
                              <FilaDeUsuario
                                key={usuario.id}
                                usuario={usuario}
                                roles={roles}
                                nombreDeCliente={nombreDeCliente}
                                onAcceso={() => {
                                  setAviso(null);
                                  setEditando(usuario);
                                }}
                                onHistorial={() => setHistorialDe(usuario)}
                                onEditarDatos={onEditarDatos ? () => onEditarDatos(usuario.id) : null}
                                onEliminar={onEliminar ? () => onEliminar(usuario.id) : null}
                              />
                            ))}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <AccesoAsignaciones
        open={editando !== null}
        onOpenChange={(open) => !open && setEditando(null)}
        token={token}
        mantisBusinessId={mantisBusinessId}
        usuario={editando}
        me={me}
        roles={roles}
        clientes={clientes}
        onGuardado={guardado}
        onRecargar={recargarEditando}
      />
      <AccesoHistorial
        open={historialDe !== null}
        onOpenChange={(open) => !open && setHistorialDe(null)}
        token={token}
        usuario={historialDe}
        roles={roles}
        nombreDeCliente={nombreDeCliente}
      />
    </div>
  );
}
